<template>
  <transition name="degrade">
    <div
      v-if="banner"
      class="sticky top-0 z-[150] flex w-full items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium text-white"
      :class="banner.class"
    >
      <AlertTriangle class="h-4 w-4 shrink-0" />
      <span>{{ banner.message }}</span>
      <!-- A shell command inside an RTL sentence reorders unless it is isolated. It is a separate
           element for the same reason SystemHealthPage marks every probe key `dir="ltr" font-mono`:
           the point of printing the command is that somebody can retype it exactly. -->
      <code v-if="banner.command" dir="ltr" class="font-mono text-xs">{{ banner.command }}</code>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { AlertTriangle } from 'lucide-vue-next'
import api, { hasAdminSession } from '@/services/api'

const banner = ref(null)
let timer = null

/**
 * Which sentence an unhealthy `/ops/status` deserves.
 *
 * There are two ways to be degraded and they are not the same news. A datastore is UNREACHABLE — the
 * platform cannot serve. Or the database SCHEMA IS BEHIND THE CODE — every service is up, `select 1`
 * answers instantly, and pages still return 500 because a column the query filters on does not exist
 * yet. That second one broke the review queue twice in two days, and «برخی سرویس‌ها در دسترس نیستند»
 * would send the reader to file an outage ticket against an infrastructure that is perfectly healthy.
 *
 * Order matters: an unreachable subsystem outranks drift, because if Postgres is down the migration
 * state could not have been read anyway.
 *
 * `d.schema` and `d.degraded` are both OMITTED — not emptied — for a caller without `ops.read`
 * (`AdminSystemHealthController::status()`), so a contracted reviewer falls through to the last branch
 * and keeps getting «…: نامشخص». That is the whole product of this banner for that seat: something is
 * wrong and it is not you. Naming the pending migrations to an outsider would tell them the deployment
 * is behind its own code, which is the same reconnaissance the subsystem names are already withheld as.
 */
const degradedBanner = (d) => {
  const names = d.degraded || []
  if (names.length) {
    return { class: 'bg-warning-action', message: 'برخی سرویس‌ها در دسترس نیستند: ' + names.join('، ') }
  }

  if (d.schema && d.schema.state === 'drifted') {
    const n = Number(d.schema.pending_count ?? 0).toLocaleString('fa-IR')
    return {
      class: 'bg-warning-action',
      message: `پایگاه‌دادهٔ این محیط از کد عقب است: ${n} مهاجرت اعمال نشده. تا اجرای این فرمان، بخش‌هایی از پنل خطای ۵۰۰ می‌دهند:`,
      command: d.schema.command,
    }
  }

  return { class: 'bg-warning-action', message: 'برخی سرویس‌ها در دسترس نیستند: نامشخص' }
}

const poll = async () => {
  // Only probe while signed in (the login screen has no session and would just 401).
  if (!hasAdminSession()) {
    banner.value = null
    return
  }

  try {
    const { data } = await api.get('/ops/status')
    const d = data?.data
    if (d && d.healthy === false) {
      banner.value = degradedBanner(d)
    } else {
      banner.value = null // recovered
    }
  } catch (e) {
    if (!e.response) {
      banner.value = { class: 'bg-error-action', message: 'ارتباط با سرور برقرار نیست — در حالِ تلاشِ مجدد…' }
    } else if (e.response.status >= 500) {
      banner.value = { class: 'bg-error-action', message: 'سرور با خطا پاسخ می‌دهد. تیمِ فنی در جریان است.' }
    }
    // 401/403 are auth concerns handled elsewhere — not an outage.
  }
}

onMounted(() => {
  poll()
  timer = setInterval(poll, 20000)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped>
.degrade-enter-active,
.degrade-leave-active {
  transition: opacity 0.2s ease;
}
.degrade-enter-from,
.degrade-leave-to {
  opacity: 0;
}
</style>
